import { useMemo } from "react";
import type { TaskBucket, TaskActionItem } from "@/services/api/agentic-v2";
import type { SurfaceSnapshot, TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import { useAgenticSurface } from "@/components/agentic-surface/useAgenticSurface";
import {
  mapApprovalLinkToJumpLink,
  mapSurfaceCardToHomeTask,
} from "@/components/agentic-surface/adapters";
import type { ApprovalJumpLink } from "@/components/home/approvalQueue";
import type { PipelineSummaryBucket } from "@/components/home/pipelineSummary";
import type { HomeTask } from "@/components/home/types";

/** Buckets merged for paraplanner matrix + legacy board (not the home review queue). */
export const MATRIX_HOME_BUCKETS: TaskBucket[] = [
  "needs_me",
  "blocked",
  "needs_review",
  "working",
];

export interface HomeSurfaceData {
  rawSnapshot: SurfaceSnapshot | null;
  needsMeTasks: TaskActionItem[];
  inProgressTasks: TaskActionItem[];
  /** Needs-attention queue: `needs_me` bucket, assignee-scoped when `assigneeUserId` is set. */
  reviewQueueTasks: HomeTask[];
  inProgressQueueTasks: HomeTask[];
  homeTasks: HomeTask[];
  approvalLinks: ApprovalJumpLink[];
  pipelineBuckets: PipelineSummaryBucket[];
}

export interface UseHomeSurfaceDataOptions {
  visibility?: VisibilityScope;
  asUserId?: string;
  assigneeUserId?: string;
  refreshKey?: number;
  enabled?: boolean;
  surfaceId?: string;
  requireWindowLive?: boolean;
}

/** Tasks can appear in multiple home buckets; dedupe before client mapping. */
function mergeHomeBucketTasks(
  buckets: Record<string, TaskSurfaceCard[] | undefined> | undefined,
): TaskSurfaceCard[] {
  const seen = new Set<string>();
  const merged: TaskSurfaceCard[] = [];
  for (const bucket of MATRIX_HOME_BUCKETS) {
    for (const card of buckets?.[bucket] ?? []) {
      const id = card._id;
      if (seen.has(id)) continue;
      seen.add(id);
      merged.push(card);
    }
  }
  return merged;
}

export function useHomeSurfaceData({
  visibility: visibilityProp,
  asUserId,
  assigneeUserId,
  refreshKey = 0,
  enabled = true,
  surfaceId,
  requireWindowLive = true,
}: UseHomeSurfaceDataOptions = {}) {
  const visibility = visibilityProp ?? "all";

  const surface = useAgenticSurface({
    surfaceId,
    visibility,
    asUserId,
    assigneeUserId,
    platform: "web",
    refreshKey,
    enabled,
    requireWindowLive,
  });

  const data = useMemo((): HomeSurfaceData | null => {
    const snap = surface.snapshot;
    if (!snap) return null;

    const matrixTasks = mergeHomeBucketTasks(snap.task_buckets);
    const needsMe = snap.task_buckets?.needs_me ?? [];
    const inProgress = snap.task_buckets?.in_progress ?? [];

    return {
      rawSnapshot: snap,
      needsMeTasks: needsMe,
      inProgressTasks: inProgress,
      reviewQueueTasks: needsMe.map(mapSurfaceCardToHomeTask),
      inProgressQueueTasks: inProgress.map(mapSurfaceCardToHomeTask),
      homeTasks: matrixTasks.map(mapSurfaceCardToHomeTask),
      approvalLinks: (snap.approval_links ?? []).map(mapApprovalLinkToJumpLink),
      pipelineBuckets: (snap.pipeline ?? []).map((row) => ({
        key: row.key as PipelineSummaryBucket["key"],
        label: row.label,
        count: row.count,
      })),
    };
  }, [surface.snapshot]);

  return {
    data,
    loading: surface.isLoading,
    isFetching: surface.isFetching,
    refreshAll: surface.refreshAll,
    error: surface.error,
  };
}

/** @deprecated Use useHomeSurfaceData */
export function useHomeDashboard(options: UseHomeSurfaceDataOptions = {}) {
  const result = useHomeSurfaceData(options);
  const snapshot = useMemo(() => {
    if (!result.data?.rawSnapshot) return null;
    const snap = result.data.rawSnapshot;
    return {
      needsMeTasks: result.data.needsMeTasks,
      matrixTasks: MATRIX_HOME_BUCKETS.flatMap(
        (bucket) => snap.task_buckets?.[bucket] ?? [],
      ),
      homeTasks: result.data.homeTasks,
      reviewQueueTasks: result.data.reviewQueueTasks,
      approvalLinks: result.data.approvalLinks,
      pipelineBuckets: result.data.pipelineBuckets,
      meetings: snap.meetings ?? [],
      homeStats: snap.home_stats ?? [],
      matrix: snap.matrix ?? null,
      feedStats: snap.feed_stats ?? null,
    };
  }, [result.data]);

  return {
    snapshot,
    loading: result.loading,
    isRefreshing: result.loading,
    refreshAll: result.refreshAll,
    error: result.error,
  };
}

export const MATRIX_HOME_BUCKETS_EXPORT = MATRIX_HOME_BUCKETS;
